import styles from './Pagination.module.css';

export interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  showTotal?: boolean;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
}

export default function Pagination({
  current,
  total,
  pageSize,
  onChange,
  showTotal = true,
  pageSizeOptions,
  onPageSizeChange,
}: PaginationProps) {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (current - 1) * pageSize + 1;
  const endItem = Math.min(current * pageSize, total);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== current) {
      onChange(page);
    }
  };

  const handlePrevious = () => handlePageChange(current - 1);
  const handleNext = () => handlePageChange(current + 1);

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Show max 7 page buttons

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination with ellipsis
      if (current <= 4) {
        // Show: 1 2 3 4 5 ... last
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (current >= totalPages - 3) {
        // Show: 1 ... last-4 last-3 last-2 last-1 last
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        // Show: 1 ... current-1 current current+1 ... last
        pages.push(1);
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const hasSizeSelector = Boolean(pageSizeOptions && onPageSizeChange);

  if (totalPages <= 1 && !hasSizeSelector) return null;

  return (
    <div className={styles.pagination}>
      {hasSizeSelector ? (
        <div className={styles.pageSizeSelector}>
          <span className={styles.label}>Показать</span>
          {pageSizeOptions!.map(size => (
            <button
              key={size}
              className={`${styles.button} ${size === pageSize ? styles.active : ''}`}
              onClick={() => size !== pageSize && onPageSizeChange!(size)}
            >
              {size}
            </button>
          ))}
          <span className={styles.label}>записей</span>
        </div>
      ) : showTotal ? (
        <span className={styles.total}>
          Показано {startItem}-{endItem} из {total} записей
        </span>
      ) : null}

      {totalPages > 1 && (
        <div className={styles.controls}>
          {/* Previous Button */}
          <button
            className={`${styles.button} ${styles.prev}`}
            onClick={handlePrevious}
            disabled={current === 1}
            aria-label='Previous page'
          >
            ‹
          </button>

          {/* Page Numbers */}
          {getPageNumbers().map((page, index) => (
            <span key={index}>
              {page === '...' ? (
                <span className={styles.ellipsis}>...</span>
              ) : (
                <button
                  className={`${styles.button} ${styles.page} ${
                    page === current ? styles.active : ''
                  }`}
                  onClick={() => handlePageChange(page as number)}
                  aria-label={`Page ${page}`}
                  aria-current={page === current ? 'page' : undefined}
                >
                  {page}
                </button>
              )}
            </span>
          ))}

          {/* Next Button */}
          <button
            className={`${styles.button} ${styles.next}`}
            onClick={handleNext}
            disabled={current === totalPages}
            aria-label='Next page'
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
